import { range } from 'es-toolkit';
import React, { useMemo } from 'react';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { RootState } from '../../store';
import { getHighVisibilityMode } from '../../store/selectors/getHighVisibilityMode';
import { getCurrentRenderMode } from '../../store/slices/camera/selectors';
import { GRID_CELL_COUNT, GRID_CELL_SIZE, GRID_HALF_WIDTH } from './designGrid';
import { Line } from './Line';

const xAxisColor = 0xf44242;
const xAxisHighVisibilityColor = 0x00ffff;
const yAxisColor = 0x42e558;
const zAxisColor = 0x4289e5;

const gridLineColor = 0x555555;

const lineCount = GRID_CELL_COUNT + 1;

const isUsingLayoutCoordinates = (state: RootState) => {
  const renderMode = getCurrentRenderMode(state);

  switch (renderMode) {
    case 'layout':
      return true;

    case 'design':
      return false;

    default:
      assertExhaustiveSwitch(renderMode);
  }
};

function XAxis() {
  const highVisibilityMode = useAppSelector(getHighVisibilityMode);

  return (
    <Line
      points={[
        [-GRID_HALF_WIDTH, 0, 0],
        [GRID_HALF_WIDTH, 0, 0],
      ]}
      color={highVisibilityMode ? xAxisHighVisibilityColor : xAxisColor}
    />
  );
}

function YAxis() {
  const topDown = useAppSelector(isUsingLayoutCoordinates);

  return (
    <Line
      points={[
        [0, -GRID_HALF_WIDTH, 0],
        [0, GRID_HALF_WIDTH, 0],
      ]}
      color={yAxisColor}
      visible={!topDown}
    />
  );
}

function ZAxis() {
  const topDown = useAppSelector(isUsingLayoutCoordinates);

  return (
    <Line
      points={[
        [0, 0, -GRID_HALF_WIDTH],
        [0, 0, GRID_HALF_WIDTH],
      ]}
      color={zAxisColor}
      visible={topDown}
    />
  );
}

function MainGrid() {
  const topDown = useAppSelector(isUsingLayoutCoordinates);
  const mainGridLines = useMemo(
    () =>
      range(lineCount).flatMap((index) => {
        const offset = index * GRID_CELL_SIZE - GRID_HALF_WIDTH;
        if (offset === 0) return null; // Do not overwrite axis lines
        return [
          <Line
            key={`x-${index}`}
            points={[
              [-GRID_HALF_WIDTH, offset, 0],
              [GRID_HALF_WIDTH, offset, 0],
            ]}
            color={gridLineColor}
          />,
          <Line
            key={`y-${index}`}
            points={[
              [offset, -GRID_HALF_WIDTH, 0],
              [offset, GRID_HALF_WIDTH, 0],
            ]}
            color={gridLineColor}
          />,
        ];
      }),
    [],
  );

  return <group rotation={topDown ? [Math.PI / 2, 0, 0] : [0, 0, 0]}>{mainGridLines}</group>;
}

export const GridVisual = () =>
  useAppSelector((state) => state.visualOptions.showGrid) ? (
    <>
      <XAxis />
      <YAxis />
      <ZAxis />
      <MainGrid />
    </>
  ) : null;
